import { DOMAIN, FIRST_PARTY_COOKIES } from "shared/SharedConstants";
import { BREAKPOINTS } from "src/lib/FrontendConstants";
import { getRequestCookies, getRequestHeaders } from "src/lib/RequestState";

/**
 * Browser-only, in-memory stand-in for functional cookies the user hasn't consented to. Features
 * keep working for the rest of the page session, and nothing is written to the device. The
 * values are written as real cookies if the user later allows functional cookies.
 */
const unconsentedCookies = new Map<string, { value: string; expiresMs?: number }>();

export const setUnconsentedCookie = (name: string, value: string | null, expiresMs?: number) => {
  if (typeof document === "undefined") return;
  if (value === null) {
    unconsentedCookies.delete(name);
  } else {
    unconsentedCookies.set(name, { value, expiresMs });
  }
};

/** Returns and clears the in-memory values, e.g. to persist them once consent is given. */
export const takeUnconsentedCookies = () => {
  const entries = [...unconsentedCookies.entries()];
  unconsentedCookies.clear();
  return entries;
};

/** Names of the cookies currently visible to this page (browser only). */
export const getDocumentCookieNames = (): string[] =>
  typeof document === "undefined"
    ? []
    : document.cookie
        .split("; ")
        .map((c) => c.split("=")[0])
        .filter(Boolean);

export const getCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") {
    return getRequestCookies()?.get(name)?.value;
  }

  const unconsented = unconsentedCookies.get(name);
  if (unconsented) {
    return unconsented.value;
  }

  const cookie = document.cookie
    .split("; ")
    .map((c) => c.split("="))
    .find(([cookieName]) => cookieName === name);

  return cookie?.slice(1).join("=");
};

/**
 * Attributes for a first-party cookie written on `url`'s host. On calmlens.com and its subdomains
 * cookies are shared across the site (`Domain`, `SameSite=Lax`, and `Secure` over HTTPS). Anywhere
 * else, such as a production build served locally, a `Domain=calmlens.com` cookie would be rejected
 * by the browser, so the cookie is host-only.
 */
export const getSiteCookieAttributes = (
  url: Pick<URL, "hostname" | "protocol">,
): { domain?: string; sameSite?: "lax"; secure?: boolean } =>
  url.hostname === DOMAIN || url.hostname.endsWith(`.${DOMAIN}`)
    ? { domain: DOMAIN, sameSite: "lax", secure: url.protocol === "https:" }
    : {};

export interface CookieAttributes {
  maxAgeSeconds?: number;
  expires?: Date;
  httpOnly?: boolean;
  domain?: string;
  sameSite?: "lax";
  secure?: boolean;
}

/**
 * A cookie as a `Set-Cookie` header value (or a `document.cookie` assignment), always on `Path=/`.
 * Pass `getSiteCookieAttributes(url)` for cookies shared across the CalmLens site.
 */
export const serializeCookie = (
  name: string,
  value: string,
  attributes: CookieAttributes = {},
): string =>
  [
    `${name}=${value}`,
    "Path=/",
    attributes.maxAgeSeconds !== undefined && `Max-Age=${attributes.maxAgeSeconds}`,
    attributes.expires && `Expires=${attributes.expires.toUTCString()}`,
    attributes.httpOnly && "HttpOnly",
    attributes.domain && `Domain=${attributes.domain}`,
    attributes.sameSite && "SameSite=Lax",
    attributes.secure && "Secure",
  ]
    .filter(Boolean)
    .join("; ");

export const getServerViewportWidth = () => {
  const headers = getRequestHeaders();
  if (!headers) {
    return undefined;
  }

  let viewportWidthFromHeaders = Number.parseInt(
    headers.get("Sec-CH-Viewport-Width") || headers.get("viewport-width") || "0",
    10,
  );
  if (Number.isNaN(viewportWidthFromHeaders)) {
    viewportWidthFromHeaders = 0;
  }

  let isMobile = false;

  if (headers.get("Sec-CH-UA-Mobile") === "?1") {
    isMobile = true;
  } else if (headers.get("sec-ch-ua-platform") === "Android") {
    isMobile = true;
  } else if (headers.get("sec-ch-ua-platform") === "iOS") {
    isMobile = true;
  }

  if (isMobile && viewportWidthFromHeaders > BREAKPOINTS.mobile.max) {
    viewportWidthFromHeaders = BREAKPOINTS.mobile.max - 1;
  }

  const viewportWidth =
    viewportWidthFromHeaders ||
    getRequestCookies()?.get(FIRST_PARTY_COOKIES.PRE_RENDERED_WIDTH.name)?.value;
  if (viewportWidth) {
    return Number.parseInt(viewportWidth as any, 10);
  }
  return undefined;
};
